/**
 * Discord logs: what happened on the Discord server, pushed in by the bot.
 *
 * Refreshes every few seconds rather than riding the SSE stream — the bot writes through an HTTP
 * route, not Redis, and a five second poll of an indexed table is cheap next to the connection
 * budget the stream already spends.
 */

import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetchDiscordEvents, fetchDiscordSummary } from "../api";
import { formatTimestamp } from "../format";
import { useDebounced } from "../hooks/useUrlFilters";
import type { DiscordEvent } from "../types";

const CATEGORIES: Array<{ id: string; label: string }> = [
  { id: "message", label: "Messages" },
  { id: "moderation", label: "Moderation" },
  { id: "honeypot", label: "Honeypot" },
  { id: "channel", label: "Channels" },
  { id: "role", label: "Roles" },
  { id: "voice", label: "Voice" },
  { id: "member", label: "Members" },
];

const ACTION_LABEL: Record<string, string> = {
  message_delete: "Message deleted",
  message_bulk_delete: "Bulk delete",
  message_edit: "Message edited",
  ban: "Banned",
  unban: "Unbanned",
  kick: "Kicked",
  timeout: "Timed out",
  timeout_remove: "Timeout removed",
  warn: "Warned",
  voice_mute: "Server muted",
  voice_unmute: "Server unmuted",
  voice_deafen: "Server deafened",
  voice_undeafen: "Server undeafened",
  voice_move: "Moved in voice",
  voice_disconnect: "Voice disconnect",
  honeypot_ban: "Honeypot ban",
  honeypot_trigger: "Honeypot trigger",
  channel_create: "Channel created",
  channel_delete: "Channel deleted",
  channel_update: "Channel updated",
  role_create: "Role created",
  role_delete: "Role deleted",
  role_update: "Role updated",
  role_add: "Role given",
  role_remove: "Role removed",
  member_join: "Joined",
  member_leave: "Left",
};

function label(action: string): string {
  return ACTION_LABEL[action] ?? action.replace(/_/g, " ");
}

function who(name: string | null, id: string | null): string {
  return name ?? id ?? "unknown";
}

function parseDetails(raw: string | null): Array<[string, string]> {
  if (!raw) return [];
  try {
    const value = JSON.parse(raw) as unknown;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        typeof v === "string" ? v : JSON.stringify(v),
      ]);
    }
    return [["details", JSON.stringify(value)]];
  } catch {
    return [["details", raw]];
  }
}

export function DiscordTab(): JSX.Element {
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [user, setUser] = useState("");
  const [search, setSearch] = useState("");
  const debouncedUser = useDebounced(user);
  const debouncedSearch = useDebounced(search);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (action) params.set("action", action);
    if (debouncedUser) params.set("user", debouncedUser);
    if (debouncedSearch) params.set("q", debouncedSearch);
    return params.toString();
  }, [category, action, debouncedUser, debouncedSearch]);

  const summary = useQuery({
    queryKey: ["discord-summary"],
    queryFn: fetchDiscordSummary,
    refetchInterval: 15_000,
  });

  const events = useInfiniteQuery({
    queryKey: ["discord", query],
    queryFn: ({ pageParam }) => fetchDiscordEvents(query, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 5_000,
  });

  const rows: DiscordEvent[] = events.data?.pages.flatMap((page) => page.events) ?? [];

  const byCategory = useMemo(() => {
    const totals = new Map<string, number>();
    for (const c of summary.data?.counts ?? []) totals.set(c.category, (totals.get(c.category) ?? 0) + c.n);
    return totals;
  }, [summary.data]);

  const actionsForCategory = useMemo(() => {
    const seen = new Set<string>();
    for (const c of summary.data?.counts ?? []) if (!category || c.category === category) seen.add(c.action);
    for (const key of Object.keys(ACTION_LABEL)) seen.add(key);
    return [...seen].sort((a, b) => label(a).localeCompare(label(b)));
  }, [summary.data, category]);

  return (
    <>
      <div className="filterbar">
        <div className="chips">
          <button type="button" className="chip" aria-pressed={category === ""} onClick={() => { setCategory(""); setAction(""); }}>
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              className="chip"
              aria-pressed={category === c.id}
              onClick={() => { setCategory(category === c.id ? "" : c.id); setAction(""); }}
              title="Events in the last 24 hours"
            >
              {c.label}
              {byCategory.get(c.id) ? ` · ${byCategory.get(c.id)}` : ""}
            </button>
          ))}
        </div>
        <span className="grow" />
        <select className="input" value={action} onChange={(e) => setAction(e.target.value)} aria-label="Filter by event type">
          <option value="">Any event</option>
          {actionsForCategory.map((a) => (
            <option key={a} value={a}>{label(a)}</option>
          ))}
        </select>
        <input
          className="input"
          placeholder="User (name or ID)"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          aria-label="Filter by user"
          style={{ width: "18ch" }}
        />
        <input
          className="input"
          placeholder="Search text, reason, channel"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search Discord logs"
          style={{ width: "26ch" }}
        />
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>By</th>
              <th>Target</th>
              <th>Channel</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {events.isLoading
                    ? "Loading…"
                    : events.isError
                      ? "Could not load Discord logs."
                      : "No Discord events match these filters yet."}
                </td>
              </tr>
            )}
            {rows.map((event) => {
              const extra = parseDetails(event.details);
              return (
                <tr key={event.id}>
                  <td className="mono">{formatTimestamp(event.occurred_at)}</td>
                  <td>
                    <span className="dtag" data-c={event.category}>{label(event.action)}</span>
                  </td>
                  <td>{event.actor_name || event.actor_id ? who(event.actor_name, event.actor_id) : <span className="mono">-</span>}</td>
                  <td>{event.target_name || event.target_id ? who(event.target_name, event.target_id) : <span className="mono">-</span>}</td>
                  <td className="mono">{event.channel_name ? `#${event.channel_name}` : "-"}</td>
                  <td className="transcript">
                    {event.reason && <div><b>Reason:</b> {event.reason}</div>}
                    {event.content && <div className="dcontent">{event.content}</div>}
                    {extra.length > 0 && (
                      <details>
                        <summary>More</summary>
                        {extra.map(([k, v]) => (
                          <div key={k}>
                            <b>{k}:</b> {v}
                          </div>
                        ))}
                      </details>
                    )}
                    {!event.reason && !event.content && extra.length === 0 && <span className="mono">-</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {events.hasNextPage && (
          <div style={{ padding: 14, textAlign: "center" }}>
            <button type="button" className="btn" disabled={events.isFetchingNextPage} onClick={() => void events.fetchNextPage()}>
              {events.isFetchingNextPage ? "Loading…" : "Load older"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
